import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { artworksService } from '../../services'
import { translate_error } from '../../utils/translate_error'
import { 
  createInitialLoadingState, 
  createAsyncReducers,
  createLoadingSelectors,
  STANDARD_OPERATIONS 
} from '../../utils/loadingStates'

// Async thunks
export const fetchArtworks = createAsyncThunk(
  'artworks/fetchArtworks',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.getArtworks(filters)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const fetchFeaturedArtworks = createAsyncThunk(
  'artworks/fetchFeaturedArtworks',
  async (limit = 12, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.getFeaturedArtworks(limit)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const fetchArtworkById = createAsyncThunk(
  'artworks/fetchArtworkById',
  async (artworkId, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.getArtworkById(artworkId)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const searchArtworks = createAsyncThunk(
  'artworks/searchArtworks',
  async ({ query, filters = {} }, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.searchArtworks(query, filters)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const fetchRelatedArtworks = createAsyncThunk(
  'artworks/fetchRelatedArtworks',
  async ({ artwork_id, category_id, limit = 6 }, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.getRelatedArtworks(artwork_id, category_id, limit)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const fetchAllArtworks = createAsyncThunk(
  'artworks/fetchAllArtworks',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.getArtworks(filters)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const fetchArtworksByCategory = createAsyncThunk(
  'artworks/fetchArtworksByCategory',
  async (category_id, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.getArtworksByCategory(category_id)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const fetchArtworksByArtist = createAsyncThunk(
  'artworks/fetchArtworksByArtist',
  async (artist_id, { rejectWithValue }) => {
    try {
      const { data, error } = await artworksService.getArtworksByArtist(artist_id)
      if (error) throw new Error(error)
      return data
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

export const incrementArtworkViews = createAsyncThunk(
  'artworks/incrementArtworkViews',
  async (artwork_id, { rejectWithValue }) => {
    try {
      const { error } = await artworksService.incrementViews(artwork_id)
      if (error) throw new Error(error)
      return artwork_id
    } catch (error) {
      return rejectWithValue(translate_error(error.message))
    }
  }
)

// Define operations for loading state tracking
const ARTWORK_OPERATIONS = {
  FETCH_ARTWORKS: 'fetchArtworks',
  FETCH_ALL: 'fetchAll',
  FETCH_FEATURED: 'fetchFeatured', 
  FETCH_ARTIST_ARTWORKS: 'fetchArtistArtworks',
  FETCH_SELECTED: 'fetchSelected',
  FETCH_RELATED: 'fetchRelated',
  SEARCH: 'search',
  ...STANDARD_OPERATIONS
};

const initialState = {
  artworks: [],
  all_artworks: [],
  featured_artworks: [],
  artist_artworks: [],
  selected_artwork: null,
  related_artworks: [],
  search_results: [],
  search_query: '',
  // Consolidated loading state
  ...createInitialLoadingState(Object.values(ARTWORK_OPERATIONS))
}

const artworksSlice = createSlice({
  name: 'artworks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSelectedArtwork: (state) => {
      state.selected_artwork = null
    },
    clearSearchResults: (state) => {
      state.search_results = []
      state.search_query = ''
    },
    setSearchQuery: (state, action) => {
      state.search_query = action.payload
    }
  },
  extraReducers: (builder) => {
    // Use consolidated loading system for all async operations
    createAsyncReducers(builder, fetchArtworks, ARTWORK_OPERATIONS.FETCH_ARTWORKS, {
      onFulfilled: (state, action) => {
        state.artworks = action.payload;
      }
    });
    
    createAsyncReducers(builder, fetchFeaturedArtworks, ARTWORK_OPERATIONS.FETCH_FEATURED, {
      onFulfilled: (state, action) => {
        state.featured_artworks = action.payload;
      }
    });
    
    createAsyncReducers(builder, fetchArtworkById, ARTWORK_OPERATIONS.FETCH_SELECTED, {
      onFulfilled: (state, action) => {
        state.selected_artwork = action.payload;
      }
    });
    
    createAsyncReducers(builder, searchArtworks, ARTWORK_OPERATIONS.SEARCH, {
      onFulfilled: (state, action) => {
        state.search_results = action.payload;
      }
    });
    
    createAsyncReducers(builder, fetchRelatedArtworks, ARTWORK_OPERATIONS.FETCH_RELATED, {
      onFulfilled: (state, action) => {
        state.related_artworks = action.payload;
      }
    });
    
    createAsyncReducers(builder, fetchAllArtworks, ARTWORK_OPERATIONS.FETCH_ALL, {
      onFulfilled: (state, action) => {
        state.all_artworks = action.payload;
      }
    });
    
    // Add remaining async operations with consolidated loading
    createAsyncReducers(builder, fetchArtworksByCategory, ARTWORK_OPERATIONS.FETCH_ARTWORKS, {
      onFulfilled: (state, action) => {
        state.artworks = action.payload;
      }
    });
    
    createAsyncReducers(builder, fetchArtworksByArtist, ARTWORK_OPERATIONS.FETCH_ARTIST_ARTWORKS, {
      onFulfilled: (state, action) => {
        state.artist_artworks = action.payload;
      }
    });
      
    // Increment artwork views
    builder.addCase(incrementArtworkViews.fulfilled, (state, action) => {
      // Update views in selected_artwork if it's the same artwork
      if (state.selected_artwork && state.selected_artwork.id === action.payload) {
        state.selected_artwork.views = (state.selected_artwork.views || 0) + 1
      }
    })
  }
})

export const { 
  clearError, 
  clearSelectedArtwork, 
  clearSearchResults, 
  setSearchQuery 
} = artworksSlice.actions

// Export normalized loading selectors
export const artworkLoadingSelectors = createLoadingSelectors('artworks');

// Convenience selectors for specific operations
export const selectIsLoadingArtworks = (state) => 
  artworkLoadingSelectors.selectIsOperationLoading(ARTWORK_OPERATIONS.FETCH_ARTWORKS)(state);

export const selectIsLoadingFeatured = (state) => 
  artworkLoadingSelectors.selectIsOperationLoading(ARTWORK_OPERATIONS.FETCH_FEATURED)(state);

export const selectIsLoadingSelected = (state) => 
  artworkLoadingSelectors.selectIsOperationLoading(ARTWORK_OPERATIONS.FETCH_SELECTED)(state);

export const selectIsSearching = (state) => 
  artworkLoadingSelectors.selectIsOperationLoading(ARTWORK_OPERATIONS.SEARCH)(state);

export const selectArtworkError = (operation) => (state) => 
  artworkLoadingSelectors.selectOperationError(operation)(state);

export default artworksSlice.reducer